// src/components/CreateEnrichmentDialog.tsx
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Stack
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../contexts/NotificationContext';
import liturgicalService from '../services/liturgicalService';
import { CreateEnrichmentPayload } from '../types/liturgical';

interface CreateEnrichmentDialogProps {
    open: boolean;
    onClose: () => void;
    selectedDate: { day: number; month: number; year: number };
    onSuccess: () => void;
}

const CreateEnrichmentDialog: React.FC<CreateEnrichmentDialogProps> = ({
    open,
    onClose,
    selectedDate,
    onSuccess
}) => {
    const { t } = useTranslation();
    const { error: showError } = useNotification();
    const [loading, setLoading] = useState(false);

    // État du formulaire
    const [celebrationType, setCelebrationType] = useState<'monthly' | 'yearly' | 'special'>('monthly');
    const [language, setLanguage] = useState<'fr' | 'en' | 'am' | 'ti'>('fr');

    // Section Saint
    const [saintName, setSaintName] = useState('');
    const [saintBiography, setSaintBiography] = useState('');
    const [saintImage, setSaintImage] = useState<File | null>(null);

    // Section Écriture
    const [scriptureReference, setScriptureReference] = useState('');
    const [scriptureText, setScriptureText] = useState('');

    // Section Méditation
    const [meditationText, setMeditationText] = useState('');

    // Section Prière
    const [prayerText, setPrayerText] = useState('');

    // Médias
    const [audioFile, setAudioFile] = useState<File | null>(null);
    const [videoFile, setVideoFile] = useState<File | null>(null);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, setter: (file: File | null) => void) => {
        const file = event.target.files?.[0];
        if (file) {
            setter(file);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);

        try {
            // Étape 1: Créer l'enrichissement sans médias
            const enrichmentData: CreateEnrichmentPayload = {
                ethiopian_month: selectedDate.month,
                ethiopian_day: selectedDate.day,
                celebration_type: celebrationType,
                ethiopian_year: celebrationType === 'yearly' ? selectedDate.year : null,

                // Section Saint
                saint_name: saintName || undefined,
                saint_biography: saintBiography || undefined,
                saint_image_url: null,

                // Section Écriture
                scripture_reference: scriptureReference || undefined,
                scripture_text: scriptureText || undefined,

                // Section Méditation
                meditation_text: meditationText || undefined,

                // Section Prière
                prayer_text: prayerText || undefined,

                // Médias
                audio_url: null,
                video_url: null,

                // Métadonnées
                language,
                is_active: true
            };

            const response = await liturgicalService.createEnrichment(enrichmentData);
            const createdEnrichment = response.data;

            // Étape 2: Upload des médias si présents
            const uploadedUrls: {
                saint_image_url?: string;
                audio_url?: string;
                video_url?: string;
            } = {};

            // Upload image du saint
            if (saintImage) {
                const uploadResponse = await liturgicalService.uploadMedia(saintImage, {
                    enrichment_id: createdEnrichment.id,
                    caption: saintName || 'Saint',
                    alt_text: `Portrait de ${saintName || 'Saint'}`
                });
                uploadedUrls.saint_image_url = uploadResponse.data.file_url;
            }

            // Upload audio
            if (audioFile) {
                const uploadResponse = await liturgicalService.uploadMedia(audioFile, {
                    enrichment_id: createdEnrichment.id,
                    caption: 'Audio de méditation'
                });
                uploadedUrls.audio_url = uploadResponse.data.file_url;
            }

            // Upload vidéo
            if (videoFile) {
                const uploadResponse = await liturgicalService.uploadMedia(videoFile, {
                    enrichment_id: createdEnrichment.id,
                    caption: 'Vidéo de méditation'
                });
                uploadedUrls.video_url = uploadResponse.data.file_url;
            }

            // Étape 3: Mettre à jour l'enrichissement avec les URLs des médias
            if (Object.keys(uploadedUrls).length > 0) {
                await liturgicalService.updateEnrichment(createdEnrichment.id, uploadedUrls);
            }

            // Succès !
            onSuccess();
            handleClose();
        } catch (err: any) {
            console.error('Erreur lors de la création de l\'enrichissement:', err);
            showError(err.response?.data?.detail || t('dialogs.createEnrichment.error'));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            // Réinitialiser le formulaire
            setCelebrationType('monthly');
            setLanguage('fr');
            setSaintName('');
            setSaintBiography('');
            setSaintImage(null);
            setScriptureReference('');
            setScriptureText('');
            setMeditationText('');
            setPrayerText('');
            setAudioFile(null);
            setVideoFile(null);
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Créer un Enrichissement Officiel
                <Typography variant="caption" display="block" color="text.secondary">
                    Pour le {selectedDate.day}/{selectedDate.month}/{selectedDate.year}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Type de célébration</InputLabel>
                        <Select
                            value={celebrationType}
                            onChange={(e) => setCelebrationType(e.target.value as any)}
                            label="Type de célébration"
                        >
                            <MenuItem value="monthly">Mensuel (chaque année)</MenuItem>
                            <MenuItem value="yearly">Annuel (cette année uniquement)</MenuItem>
                            <MenuItem value="special">Spécial</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel>Langue</InputLabel>
                        <Select
                            value={language}
                            onChange={(e) => setLanguage(e.target.value as any)}
                            label="Langue"
                        >
                            <MenuItem value="fr">Français</MenuItem>
                            <MenuItem value="en">English</MenuItem>
                            <MenuItem value="am">አማርኛ</MenuItem>
                            <MenuItem value="ti">ትግርኛ</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* Section Saint */}
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Section Saint (Optionnel)</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                label="Nom du Saint"
                                value={saintName}
                                onChange={(e) => setSaintName(e.target.value)}
                                placeholder="Ex: Saint Jean"
                            />
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Biographie du Saint"
                                value={saintBiography}
                                onChange={(e) => setSaintBiography(e.target.value)}
                                placeholder="Biographie et histoire du saint..."
                            />
                            <Box>
                                <input
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    id="saint-image-upload"
                                    type="file"
                                    onChange={(e) => handleFileChange(e, setSaintImage)}
                                />
                                <label htmlFor="saint-image-upload">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<CloudUploadIcon />}
                                    >
                                        Image du Saint
                                    </Button>
                                </label>
                                {saintImage && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                        <Typography variant="body2">{saintImage.name}</Typography>
                                        <IconButton size="small" onClick={() => setSaintImage(null)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                {/* Section Écriture */}
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Section Écriture (Optionnel)</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                label="Référence Biblique"
                                value={scriptureReference}
                                onChange={(e) => setScriptureReference(e.target.value)}
                                placeholder="Ex: Jean 3:16"
                            />
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Texte de l'Écriture"
                                value={scriptureText}
                                onChange={(e) => setScriptureText(e.target.value)}
                                placeholder="Car Dieu a tant aimé le monde..."
                            />
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                {/* Section Méditation */}
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Section Méditation (Principale)</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TextField
                            fullWidth
                            multiline
                            rows={6}
                            label="Texte de Méditation"
                            value={meditationText}
                            onChange={(e) => setMeditationText(e.target.value)}
                            placeholder="Réflexion du jour..."
                        />
                    </AccordionDetails>
                </Accordion>

                {/* Section Prière */}
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Section Prière (Optionnel)</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Texte de Prière"
                            value={prayerText}
                            onChange={(e) => setPrayerText(e.target.value)}
                            placeholder="Seigneur, guide-nous..."
                        />
                    </AccordionDetails>
                </Accordion>

                {/* Section Médias */}
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Médias Supplémentaires (Optionnel)</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2}>
                            {/* Audio */}
                            <Box>
                                <input
                                    accept="audio/*"
                                    style={{ display: 'none' }}
                                    id="audio-upload"
                                    type="file"
                                    onChange={(e) => handleFileChange(e, setAudioFile)}
                                />
                                <label htmlFor="audio-upload">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<CloudUploadIcon />}
                                    >
                                        Audio de Méditation
                                    </Button>
                                </label>
                                {audioFile && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                        <Typography variant="body2">{audioFile.name}</Typography>
                                        <IconButton size="small" onClick={() => setAudioFile(null)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>

                            {/* Vidéo */}
                            <Box>
                                <input
                                    accept="video/*"
                                    style={{ display: 'none' }}
                                    id="video-upload"
                                    type="file"
                                    onChange={(e) => handleFileChange(e, setVideoFile)}
                                />
                                <label htmlFor="video-upload">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<CloudUploadIcon />}
                                    >
                                        Vidéo de Méditation
                                    </Button>
                                </label>
                                {videoFile && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                        <Typography variant="body2">{videoFile.name}</Typography>
                                        <IconButton size="small" onClick={() => setVideoFile(null)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Annuler
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={20} />}
                >
                    {loading ? 'Création...' : 'Créer l\'Enrichissement'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateEnrichmentDialog;
